import React, { useEffect, useMemo, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useNavigate } from "react-router-dom";
import { usePostUpdate } from "../Hooks/usePostUpdate";
import useIdleTimeout from "../Hooks/useIdleTimeout";
import IdleTimeoutDialog from "../components/dialogs/IdleTimeoutDialog";
import { Cookies } from "react-cookie-consent";
import { mfaEnabled, mfaLoggedIn } from "../utils/userFunctions";
import { QueryClient, useQueryClient } from "react-query";

type Props = {
  children: JSX.Element;
};

export type CompanyDetails = {
  companyName: string;
  tokenAccess: string;
  tokenRefresh: string;
  company_kyc_form_signed: boolean;
  referenceNumber: string;
  isBannedCompany: boolean;
  documentsVerified: boolean;
};

export type UserDetails = {
  email: string;
  name: string;
  kyc_information_added: boolean;
  isLoggedIn: boolean;
  hasCompany: boolean;
  isMainUser: boolean;
  otp_enabled: boolean;
  otp_verified: boolean;
  otp_validated: boolean;
  otp_email_enabled: boolean;
  otp_email_verified: boolean;
  primary_2fa_method: string;
  roles: string[];
};
export type Auth = {
  companyDetails: CompanyDetails;
  userDetails: UserDetails;
  logout: (link?: string, isNavigate?: boolean) => void;
  setCompany: (state: CompanyDetails) => void;
  setUser: (state: UserDetails) => void;
  homeNavigation: () => void;
  navstatus: boolean;
  resetContext: () => void;
};
export const companyDetails: CompanyDetails = {
  companyName: "",
  tokenAccess: "",
  tokenRefresh: "",
  referenceNumber: "",
  company_kyc_form_signed: false,
  isBannedCompany: false,
  documentsVerified: false,
};

export const userDetails: UserDetails = {
  email: "",
  name: "",
  kyc_information_added: false,
  isLoggedIn: false,
  hasCompany: false,
  isMainUser: false,
  otp_enabled: false,
  otp_verified: false,
  otp_validated: false,
  otp_email_enabled: false,
  otp_email_verified: false,
  primary_2fa_method: "",
  roles: [],
};

const auth: Auth = {
  companyDetails,
  userDetails,
  logout: (link?: string, isNavigate?: boolean) => {},
  setCompany: (state: CompanyDetails) => {},
  setUser: (state: UserDetails) => {},
  homeNavigation: () => {},
  navstatus: false,
  resetContext: () => {},
};

const AuthContext = React.createContext(auth);

export const AuthContextProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [data2, callApi2, isLoading2] = usePostUpdate();
  const [navLink, setNavLink] = useState("");
  const query = useQueryClient();
  const queryClient = new QueryClient();

  const [company, setCompany] = useState<CompanyDetails>(companyDetails);

  const [user, setUser] = useState<UserDetails>(userDetails);

  const logoutHandler = (link = "/", isNavigate = true) => {
    callApi2(
      { refresh_token: company.tokenRefresh },
      "auth/logout/",
      company.tokenAccess
    );

    setCompany(companyDetails);

    setUser(userDetails);

    secureLocalStorage.clear();
    Cookies.remove("tokenRefresh");
    Cookies.remove("tokenAccess");
    Cookies.remove("otpValidated");
    query.clear();
    queryClient.clear();
    if (isNavigate) navigate(link);
    idleTimer.pause();
  };

  useEffect(() => {
    if (user.email && navLink.length > 0) {
      navigate(navLink);
      setNavLink("");
    }
  }, [navLink, user]);

  const homeNavigation = () => {
    if (user.isLoggedIn && user?.roles?.includes("client_company")) {
      if (user.hasCompany) {
        if (company.documentsVerified) {
          navigate("/dashboard");
        } else if (!company.company_kyc_form_signed) {
          navigate("/kyc-documents");
        } else {
          navigate("/verification");
        }
      } else {
        navigate("/company-setup");
      }
    } else if (user.isLoggedIn && user?.roles?.includes("client_individual")) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  const resetContext = () => {
    setCompany(companyDetails);
    setUser(userDetails);
  };

  // idle timeout
  const handleIdle = () => {
    setOpenModal(true);
  };
  const [openModal, setOpenModal] = useState(false);

  // navbar status
  const navstatus = useMemo(() => {
    if (mfaEnabled(user)) return mfaLoggedIn(user);
    // NOTE: not sure about how this logic affects the b2c navbar
    if (user?.roles?.includes("client_individual")) {
      //return user.isLoggedIn;
      return user.kyc_information_added && company.documentsVerified;
    }

    return user.isLoggedIn && user.hasCompany && company.documentsVerified;
  }, [user, company]);

  // logout user and pause timer
  const handleLogout = () => {
    logoutHandler();
    setOpenModal(false);
    idleTimer.pause();
  };

  // keep user logged in
  const stayLoggedIn = () => {
    setOpenModal(false);
    idleTimer.reset();
  };
  // call userIdleTimer Hook
  const { idleTimer } = useIdleTimeout({
    onIdle: handleIdle,
    // this converts to 900,000 milliseconds -> 15min
    idleTime: 60,
    // disabled: !logged,
    disabled: true,
    handleLogout: handleLogout,
  });

  // end of idletimeout

  return (
    <AuthContext.Provider
      value={{
        companyDetails: company,
        userDetails: user,
        logout: logoutHandler,
        setCompany: setCompany,
        setUser: setUser,
        homeNavigation: homeNavigation,
        navstatus: navstatus,
        resetContext: resetContext,
      }}
    >
      {children}
      {user.isLoggedIn && openModal && (
        <IdleTimeoutDialog
          stayLoggedIn={stayLoggedIn}
          logout={handleLogout}
          open={openModal}
          idleTimer={idleTimer}
        />
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;
